import { useDeferredValue, useMemo, useState, useTransition } from "react";
import { runNonUrgent } from "@/lib/interaction";
import { RESOURCES, RESOURCE_CATEGORIES, type DownloadableResource } from "@/content/resources";
import { EyebrowLabel } from "@/components/ui-ext/Badge";

const MIME: Record<DownloadableResource["format"], string> = {
  md: "text/markdown;charset=utf-8",
  csv: "text/csv;charset=utf-8",
};

function download(resource: DownloadableResource) {
  const blob = new Blob([resource.body], { type: MIME[resource.format] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${resource.slug}.${resource.format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Ungated download library. Everything is generated client-side from static
 * strings, so the page stays fully static/cacheable and there is no form,
 * no email capture, and no network round-trip on download.
 */
export function ResourceLibrary() {
  const [category, setCategory] = useState<string>("All");
  const [copied, setCopied] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  // The chip press updates instantly (aria-pressed / styling); the list
  // re-render is deferred so the interaction paints within the INP budget.
  const deferredCategory = useDeferredValue(category);
  const filtered = useMemo(
    () =>
      deferredCategory === "All"
        ? RESOURCES
        : RESOURCES.filter((r) => r.category === deferredCategory),
    [deferredCategory],
  );

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter resources by category">
        {["All", ...RESOURCE_CATEGORIES].map((c) => {
          const active = c === category;
          return (
            <button
              key={c}
              type="button"
              onClick={() => startTransition(() => setCategory(c))}
              aria-pressed={active}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {filtered.map((r) => (
          <article
            key={r.slug}
            className="flex flex-col rounded-3xl border border-border bg-card/40 p-7 transition-colors hover:border-primary/40"
          >
            <div className="flex items-center gap-3 text-[0.65rem] uppercase tracking-widest">
              <span className="text-primary">{r.category}</span>
              <span className="text-muted-foreground">
                .{r.format} · {r.size}
              </span>
            </div>
            <h3 className="mt-3 font-display text-2xl tracking-tight">{r.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.summary}</p>
            <ul className="mt-5 flex-1 space-y-1.5 text-sm text-foreground/85">
              {r.useCases.map((u) => (
                <li key={u} className="flex gap-2">
                  <span aria-hidden className="text-primary">
                    ·
                  </span>
                  {u}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => runNonUrgent(() => download(r))}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                Download .{r.format} <span aria-hidden>↓</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  // Optimistic label first, clipboard write after the paint.
                  setCopied(r.slug);
                  runNonUrgent(() => {
                    void navigator.clipboard?.writeText(r.body);
                  });
                  setTimeout(() => setCopied((c) => (c === r.slug ? null : c)), 2000);
                }}
                className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
              >
                {copied === r.slug ? "Copied" : "Copy contents"}
              </button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              No email required. Downloads are generated in your browser.
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}
